

export type MfaChannel = 'sms' | 'email';

export interface ApiEnvelope<T> {
  responseStatus: number;
  message?: string;
  result?: T;
}

/** Login stopped at the second factor; nothing is stored until Mfa/Verify succeeds. */
export interface MfaPendingResult {
  requiresMfa: true;
  challengeId: string;
  channels: MfaChannel[];
  maskedPhone: string | null;
  maskedEmail: string | null;
  /** False when the first code could not be delivered; `sendMessage` says why. */
  sent?: boolean;
  sendMessage?: string | null;
}

export interface LoginUserDetails {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  phoneNumber: string | null;
  agency: unknown;
  twoFactorEnabled: boolean;
  isForcePasswordChange: boolean;
  localSupplier: { id: string; name: string } | null;
  suppliers: { id: string; name: string }[] | null;
  supplierBankSitesData?: unknown;
}

export interface RolePermissionModule {
  moduleType: string;
  [key: string]: unknown;
}

export interface LoginRoleDetails {
  name: string;
  roleType: number;
  permissions: RolePermissionModule[];
  [key: string]: unknown;
}

export interface AccountSitesGroup {
  accountId: string;
  accountName: string;
  sites: { siteId: string; siteName: string }[];
}

export interface LoginSuccessResult {
  requiresMfa?: false;
  access_token: string;
  refreshToken: string;
  userdetails: LoginUserDetails;
  userRoleDetails: LoginRoleDetails;
  operator: AccountSitesGroup[];
  supplierBank: AccountSitesGroup[];
  hasPasskey?: boolean;
  /** True when the user has no passkey and this device is not inside a "Not now" window (90 days on a trusted device). */
  promptPasskeySetup?: boolean;
}

export interface PasskeySnoozeResult {
  /** False on an unrecognised device: nothing was stored, the next sign-in asks again. */
  snoozed: boolean;
  snoozedUntil: string | null;
}

export interface PasskeyOptionsResult {
  challengeId: string;
  /** WebAuthn options as produced by the server (base64url byte fields), to be parsed client-side. */
  optionsJson: string;
}

export interface PasskeyRegisterCompleteRequest {
  challengeId: string;
  attestationResponseJson: string;
  friendlyName?: string;
  /** The portal adding the passkey; it names the portal in the confirmation email. */
  siteType: string;
}

/** What register/complete returns, so the "Passkey added" screen can name where the passkey went. */
export interface PasskeyRegisterCompleteResult {
  /** The app holding the new passkey, e.g. "Dashlane"; null when the provider is unknown. */
  providerName: string | null;
}

export interface PasskeyLoginCompleteRequest {
  challengeId: string;
  assertionResponseJson: string;
  siteType: string;
  deviceFingerprint?: string;
}

/**
 * Every gate before the app has the same shape: the sign-in stops, nothing is stored, and the
 * result says which step runs next. Terms sign-off is the last of them.
 */
export type LoginResult =
  | MfaPendingResult
  | PhoneVerificationRequiredResult
  | TermsAcceptanceRequiredResult
  | LoginSuccessResult;

/** Which agreement is due. The body arrives separately, from TermsAcceptance/Pending. */
export interface TermsGateInfo {
  termsId: string;
  termsType: number;
  versionLabel: string;
}

/**
 * Identity, 2FA and the device check have passed, but the user has not agreed to the live version
 * of their audience's terms. No token is issued until they do, so declining leaves nothing behind.
 */
export interface TermsAcceptanceRequiredResult {
  requiresTermsAcceptance: true;
  termsToken: string;
  terms: TermsGateInfo;
}

/** The agreement as the acceptance screen renders it. */
export interface PendingTerms extends TermsGateInfo {
  firstName: string;
  contentHtml: string;
}

export interface AcceptTermsRequest {
  token: string;
  /** The version the screen displayed; the API refuses one that is no longer live. */
  termsId: string;
  siteType: string;
  deviceFingerprint?: string | null;
}

/** A published policy, read without signing in — what the footer links open. */
export interface PublicPolicy {
  termsType: number;
  title: string;
  versionLabel: string;
  contentHtml: string;
}

/** Mirrors the API's TermsType. Only these two are readable without a token. */
export enum PolicyType {
  PrivacyPolicy = 3,
  CookiePolicy = 4,
}

/**
 * First factor passed but the mobile number is not confirmed yet: the mobile-verify step runs next. Verifying the
 * code confirms the phone, turns on 2FA and completes the sign-in. Nothing is stored until then.
 */
export interface PhoneVerificationRequiredResult {
  requiresPhoneVerification: true;
  challengeId: string;
  email: string;
  firstName: string | null;
  lastName: string | null;
  phoneNumber: string | null;
  phoneCountryCode: string | null;
  /** US-only roles: the country code is fixed to +1 and hidden. */
  phoneLocked: boolean;
}

/** The mobile-verify step as kept across a refresh: the code step resumes when a code was already sent. */
export interface PendingPhoneVerification extends PhoneVerificationRequiredResult {
  codeSentTo?: string | null;
}

export interface PhoneVerifySendRequest {
  challengeId: string;
  firstName: string;
  lastName: string;
  phoneNumber: string;
  phoneCountryCode: string | null;
}

export type LoginMethod = 'password' | 'passkey';

export interface LoginStartResult {
  methods: LoginMethod[];
  /** True when this browser is a trusted device for the account. */
  returning: boolean;
  displayEmail: string;
  /** Supplier accounts sign in on the supplier portal; this door shows them the way across. */
  isSupplier: boolean;
}

export interface LoginPasswordRequest {
  userName: string;
  password: string;
  siteType: string;
  deviceFingerprint: string;
}

export function isMfaPending(result: LoginResult): result is MfaPendingResult {
  return (result as MfaPendingResult).requiresMfa === true;
}

export function isTermsAcceptanceRequired(result: LoginResult): result is TermsAcceptanceRequiredResult {
  return (result as TermsAcceptanceRequiredResult).requiresTermsAcceptance === true;
}

export function isPhoneVerificationRequired(result: LoginResult): result is PhoneVerificationRequiredResult {
  return (result as PhoneVerificationRequiredResult).requiresPhoneVerification === true;
}

export interface PendingMfaChallenge {
  challengeId: string;
  email: string;
  channels: MfaChannel[];
  maskedPhone: string | null;
  maskedEmail: string | null;
  sent?: boolean;
  sendMessage?: string | null;
}

export interface MfaSendRequest {
  challengeId: string;
  channel: MfaChannel;
}

export interface MfaVerifyRequest {
  challengeId: string;
  code: string;
  /** Remember this device so the second factor is skipped on it for a while. */
  trustDevice?: boolean;
  /** What this browser says it is ("Chrome on Android (Pixel 8)"); only sent with trustDevice. */
  deviceLabel?: string;
}

export enum ActivationStep {
  NotStarted = 0,
  IdentityVerified = 1,
  PhoneVerified = 2,
  EmailVerified = 3,
  Completed = 4,
}

/** Wizard order: details → email code → phone code → passkey or password. Driven by the server. */
export type ActivationNextStep = 'details' | 'email' | 'phone' | 'secure' | 'done';

export interface ActivationStatus {
  step: ActivationStep;
  nextStep: ActivationNextStep;
  email: string;
  firstName: string;
  lastName: string;
  /** Full number on file, to pre-fill the details form ("Go back"). */
  phoneNumber: string | null;
  /** A code for the current code step is still valid: a refresh reuses it instead of sending another. */
  codeActive: boolean;
  /** Set when finishing the wizard signed the user in, or the terms-pending result when an agreement is owed first. */
  login?: LoginSuccessResult | TermsAcceptanceRequiredResult | null;
  /** Set when this call registered a passkey: the app holding it ("Dashlane"), or null when the provider is unknown. */
  passkeyProviderName?: string | null;
  maskedPhone: string | null;
  maskedEmail: string | null;
  phoneCountryCode: string;
  phoneLocked: boolean;
}

/** Sent with the request that finishes the wizard, so the API can sign the user in to this portal. */
export interface ActivationFinish {
  siteType: string;
  deviceFingerprint: string;
}

export interface ActivationIdentityRequest {
  token: string;
  /** The portal running the wizard, so the code SMS names it. */
  siteType: string;
  firstName: string;
  lastName: string;
  phone: string;
  phoneCountryCode?: string;
}

export interface TrustedDeviceSummary {
  id: string;
  userAgentHint: string | null;
  /** Name the browser reported for itself when trusted; null on devices trusted before it was recorded. */
  deviceLabel?: string | null;
  lastIpAddress: string | null;
  trustedOn: string;
  lastSeenOn: string;
  isCurrent: boolean;
}

/** Error payload for Mfa/Verify: `restart` means the challenge is dead and login must start over. */
export interface MfaVerifyFailure {
  restart: boolean;
}

export function isMfaVerifyFailure(result: object): result is MfaVerifyFailure {
  return 'restart' in result;
}
